// Adapter para conectar MUI Chat con Supabase
// Implementa la interfaz ChatAdapter de @mui/x-chat
// Transforma datos entre formato MUI y formato Supabase V2

import { supabase } from '../lib/supabase';

// Caché global para getConversationMembers
const conversationMembersCache = new Map();

/**
 * Obtiene información de miembros de una conversación para MUI Chat
 * Incluye el usuario actual y el otro participante
 * Usa caché para evitar consultas repetidas
 */
async function getConversationMembers(conversationId, currentUserId) {
  // Verificar caché primero
  const cacheKey = `${conversationId}_${currentUserId}`;
  if (conversationMembersCache.has(cacheKey)) {
    return conversationMembersCache.get(cacheKey);
  }

  try {
    // Obtener la conversación para identificar participantes
    const { data: conversation, error: convError } = await supabase
      .from('conversations_v2')
      .select('participant_a, participant_b')
      .eq('id', conversationId)
      .single();

    if (convError || !conversation) {
      return null;
    }

    // Determinar el otro participante
    const otherParticipantId = conversation.participant_a === currentUserId 
      ? conversation.participant_b 
      : conversation.participant_a;

    // Obtener perfiles de ambos participantes
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', [currentUserId, otherParticipantId]);

    if (!profiles || profiles.length === 0) {
      return null;
    }

    const profileMap = new Map(profiles.map(p => [p.id, p]));

    // Construir array de miembros para MUI Chat
    // MUI Chat espera roles 'user' para el usuario actual
    const members = [
      {
        id: currentUserId,
        displayName: profileMap.get(currentUserId)?.full_name || 'Tú',
        role: 'user',
        avatarUrl: null // Se puede implementar más adelante
      },
      {
        id: otherParticipantId,
        displayName: profileMap.get(otherParticipantId)?.full_name || 'Usuario',
        role: 'assistant', // MUI Chat requiere este rol para el otro participante
        avatarUrl: null // Se puede implementar más adelante
      }
    ];

    const result = {
      members,
      currentUser: {
        id: currentUserId,
        displayName: profileMap.get(currentUserId)?.full_name || 'Tú',
        role: 'user'
      }
    };

    // Guardar en caché
    conversationMembersCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error obteniendo miembros:', error);
    return null;
  }
}

/**
 * Limpia el caché de miembros para una conversación específica
 */
function clearConversationMembersCache(conversationId, currentUserId) {
  const cacheKey = `${conversationId}_${currentUserId}`;
  conversationMembersCache.delete(cacheKey);
}

/**
 * Transforma mensaje de MUI Chat a formato Supabase
 */
function transformMuiToSupabase(muiMessage, conversationId, senderId) {
  // Extraer texto del mensaje de MUI Chat
  let textContent = '';
  
  // Intentar diferentes formatos de content
  if (muiMessage.parts && Array.isArray(muiMessage.parts)) {
    // Formato con parts directamente en el mensaje
    const textPart = muiMessage.parts.find(part => part.type === 'text');
    textContent = textPart?.text || '';
  } else if (muiMessage.content && typeof muiMessage.content === 'object') {
    // Si content es un objeto con parts
    if (muiMessage.content.parts && Array.isArray(muiMessage.content.parts)) {
      const textPart = muiMessage.content.parts.find(part => part.type === 'text');
      textContent = textPart?.text || '';
    }
  } else if (typeof muiMessage.content === 'string') {
    // Si content es un string directo
    textContent = muiMessage.content;
  } else if (typeof muiMessage === 'string') {
    // Si el mensaje entero es un string
    textContent = muiMessage;
  }
  
  // Construir objeto content en formato JSONB para Supabase
  const content = {
    parts: [
      {
        type: 'text',
        text: textContent
      }
    ]
  };
  
  return {
    conversation_id: conversationId,
    sender_id: senderId,
    content: content,
    message_type: 'text',
    reply_to_message_id: muiMessage.replyTo?.id || null
  };
}

/**
 * Adapter Supabase para MUI Chat
 * Implementa la interfaz ChatAdapter
 */
export function createSupabaseMuiAdapter(session, conversationId = null) {
  const currentUserId = session?.user?.id;
  let membersCache = null;
  let currentUserCache = null;
  let profilesCache = new Map(); // Caché de perfiles para evitar consultas repetidas

  // Cargar miembros si hay conversationId
  if (conversationId) {
    getConversationMembers(conversationId, currentUserId).then(result => {
      if (result) {
        membersCache = result.members;
        currentUserCache = result.currentUser;
        // Cachear perfiles de miembros
        result.members.forEach(member => {
          profilesCache.set(member.id, { displayName: member.displayName });
        });
      }
    });
  }

  const adapter = {
    /**
     * Enviar mensaje a Supabase usando V2
     */
    async sendMessage({ message, signal }) {
      if (!currentUserId) {
        throw new Error('Usuario no autenticado');
      }

      const supabaseMessage = transformMuiToSupabase(
        message,
        conversationId,
        currentUserId
      );

      try {
        // Enviar mensaje usando RPC V2 send_message_v2
        // Usar formato de parámetro único JSONB para evitar problemas de caché
        const params = {
          params: {
            conversation_id: conversationId,
            content: supabaseMessage.content,
            message_type: supabaseMessage.message_type || 'text',
            reply_to_message_id: supabaseMessage.reply_to_message_id || null
          }
        };
        
        const { data, error } = await supabase.rpc('send_message_v2', params);

        if (error) {
          throw error;
        }

        // Retornar un stream vacío que se cierra inmediatamente
        // En chats entre humanos, no hay respuestas automáticas del sistema
        return new ReadableStream({
          start(controller) {
            controller.close();
          }
        });
      } catch (error) {
        console.error('Error enviando mensaje:', error);
        throw error;
      }
    },

    /**
     * Listar mensajes de una conversación usando V2
     * Usa consulta directa GET en lugar de RPC
     */
    async listMessages({ conversationId: convId, cursor }) {
      if (!convId) {
        return { messages: [], cursor: null, hasMore: false };
      }

      try {
        // Usar consulta directa a la tabla messages_v2 con JOIN
        const { data, error } = await supabase
          .from('messages_v2')
          .select(`
            id,
            conversation_id,
            sender_id,
            content,
            message_status,
            message_type,
            created_at,
            is_edited,
            edited_at,
            reply_to_message_id,
            reply_to_content,
            profiles!messages_v2_sender_id_fkey (
              full_name
            )
          `)
          .eq('conversation_id', convId)
          .eq('is_deleted', false)
          .order('created_at', { ascending: true })
          .range(cursor || 0, (cursor || 0) + 49);

        if (error) {
          throw error;
        }

        // Transformar datos de V2 a formato MUI
        // MUI Chat usa roles 'user' y 'assistant', pero personalizaremos display con roleDisplayNames
        const messages = data.map(msg => ({
          id: msg.id,
          conversationId: msg.conversation_id,
          role: msg.sender_id === currentUserId ? 'user' : 'assistant',
          status: msg.message_status,
          parts: msg.content?.parts || [],
          createdAt: msg.created_at,
          isEdited: msg.is_edited,
          replyTo: msg.reply_to_message_id ? {
            id: msg.reply_to_message_id,
            content: msg.reply_to_content
          } : null,
          reactions: [] // Se puede implementar más adelante si es necesario
        }));

        return {
          messages,
          cursor: cursor ? cursor + 50 : 50,
          hasMore: data.length === 50
        };
      } catch (error) {
        console.error('Error listando mensajes:', error);
        return { messages: [], cursor: null, hasMore: false };
      }
    },

    /**
     * Listar conversaciones del usuario usando V2
     * Usa consulta directa GET en lugar de RPC
     */
    async listConversations({ cursor }) {
      // Actualizar miembros cache cuando se listan conversaciones
      // Esto asegura que siempre tengamos los datos más recientes
      return this._listConversationsInternal({ cursor });
    },

    /**
     * Método interno para listar conversaciones
     */
    async _listConversationsInternal({ cursor }) {
      if (!currentUserId) {
        return { conversations: [], cursor: null, hasMore: false };
      }

      try {
        // Primero obtener conversaciones del usuario
        const { data: conversations, error } = await supabase
          .from('conversations_v2')
          .select(`
            id,
            participant_a,
            participant_b,
            last_message_at,
            unread_count_a,
            unread_count_b,
            is_pinned,
            is_muted,
            is_archived,
            updated_at
          `)
          .or(`participant_a.eq.${currentUserId},participant_b.eq.${currentUserId}`)
          .order('last_message_at', { ascending: false, nullsFirst: false });

        if (error) {
          throw error;
        }

        // Obtener perfiles de los participantes
        const participantIds = new Set();
        conversations.forEach(conv => {
          participantIds.add(conv.participant_a);
          participantIds.add(conv.participant_b);
        });

        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', Array.from(participantIds));

        const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

        // Obtener avatares de user_images para los participantes
        // Optimización: una sola consulta para todos los participantes en lugar de N+1
        const avatarMap = new Map();
        try {
          // Obtener todos los álbumes "fotosDePerfil" de los participantes en una sola consulta
          const { data: albums } = await supabase
            .from('albums')
            .select('id, user_id')
            .eq('name', 'fotosDePerfil')
            .eq('type', 1)
            .in('user_id', Array.from(participantIds));

          if (albums && albums.length > 0) {
            const albumIds = albums.map(a => a.id);
            
            // Obtener todas las imágenes de estos álbumes en una sola consulta
            const { data: albumImages } = await supabase
              .from('album_images')
              .select(`
                album_id,
                user_images (
                  url
                )
              `)
              .in('album_id', albumIds)
              .eq('user_images.is_deleted', false)
              .order('created_at', { ascending: false, foreignTable: 'user_images' });

            // Agrupar imágenes por album_id y tomar la primera de cada álbum
            const imagesByAlbum = new Map();
            albumImages?.forEach(img => {
              if (!imagesByAlbum.has(img.album_id)) {
                imagesByAlbum.set(img.album_id, img.user_images?.url);
              }
            });

            // Mapear user_id a avatar URL
            albums.forEach(album => {
              const avatarUrl = imagesByAlbum.get(album.id);
              if (avatarUrl) {
                avatarMap.set(album.user_id, avatarUrl);
              }
            });
          }
        } catch (error) {
          console.error('Error obteniendo avatares:', error);
        }

        // Obtener último mensaje de cada conversación
        const conversationIds = conversations.map(c => c.id);
        const { data: lastMessages } = await supabase
          .from('messages_v2')
          .select('conversation_id, content, created_at')
          .in('conversation_id', conversationIds)
          .order('created_at', { ascending: false });

        // Agrupar últimos mensajes por conversation_id
        const lastMessageMap = new Map();
        lastMessages?.forEach(msg => {
          if (!lastMessageMap.has(msg.conversation_id)) {
            lastMessageMap.set(msg.conversation_id, msg);
          }
        });

        // Transformar datos de V2 a formato MUI
        const transformedConversations = conversations.map(conv => {
          // Determinar el otro participante (no el usuario actual)
          const isParticipantA = conv.participant_a === currentUserId;
          const otherParticipantId = isParticipantA ? conv.participant_b : conv.participant_a;
          const otherParticipant = profileMap.get(otherParticipantId);
          const unreadCount = isParticipantA ? conv.unread_count_a : conv.unread_count_b;
          
          // Obtener el último mensaje
          const lastMessage = lastMessageMap.get(conv.id);

          return {
            id: conv.id,
            title: otherParticipant?.full_name || 'Usuario desconocido',
            preview: lastMessage?.content?.parts?.[0]?.text || 'Sin mensajes',
            timestamp: conv.last_message_at || new Date().toISOString(),
            unreadCount: unreadCount || 0,
            participantId: otherParticipantId,
            avatar: avatarMap.get(otherParticipantId) || null,
            isPinned: conv.is_pinned,
            isMuted: conv.is_muted,
            isArchived: conv.is_archived
          };
        });

        return {
          conversations: transformedConversations,
          cursor: null,
          hasMore: false
        };
      } catch (error) {
        console.error('Error listando conversaciones:', error);
        return { conversations: [], cursor: null, hasMore: false };
      }
    },

    /**
     * Marcar mensajes como leídos en V2
     * Actualiza el campo de lectura correspondiente según el participante
     */
    async markRead({ conversationId: convId, messageId }) {
      if (!convId || !currentUserId) return;

      try {
        // Obtener la conversación para determinar qué campo actualizar
        const { data: conversation } = await supabase
          .from('conversations_v2')
          .select('participant_a, participant_b')
          .eq('id', convId)
          .single();

        if (!conversation) return;

        // Determinar qué campo de lectura actualizar
        const isParticipantA = conversation.participant_a === currentUserId;
        const updateField = isParticipantA ? 'read_by_a' : 'read_by_b';
        const timestampField = isParticipantA ? 'read_at_a' : 'read_at_b';

        // Actualizar el mensaje
        await supabase
          .from('messages_v2')
          .update({
            [updateField]: true,
            [timestampField]: new Date().toISOString()
          })
          .eq('id', messageId);
      } catch (error) {
        console.error('Error marcando mensaje como leído:', error);
      }
    },

    /**
     * Reconectar a stream (no implementado en V1/V2 actual)
     */
    async reconnectToStream({ conversationId, messageId, signal }) {
      return null;
    }
  };

  // Exponer métodos para obtener miembros y usuario actual
  adapter.getMembers = () => membersCache;
  adapter.getCurrentUser = () => currentUserCache;
  let isRefreshingMembers = false; // Bandera para evitar llamadas simultáneas
  adapter.refreshMembers = async () => {
    if (conversationId && !isRefreshingMembers) {
      isRefreshingMembers = true;
      try {
        const result = await getConversationMembers(conversationId, currentUserId);
        if (result) {
          membersCache = result.members;
          currentUserCache = result.currentUser;
        }
      } finally {
        isRefreshingMembers = false;
      }
    }
  };

  return adapter;
}

/**
 * Adapter para modo real-time con Supabase
 * Implementa métodos adicionales para actualizaciones en tiempo real
 */
export function createSupabaseRealtimeAdapter(session, conversationId = null) {
  const baseAdapter = createSupabaseMuiAdapter(session, conversationId);
  const currentUserId = session?.user?.id;

  return {
    ...baseAdapter,

    /**
     * Suscribirse a actualizaciones de conversación usando V2
     */
    subscribe({ conversationId: convId, onUpdate }) {
      if (!convId) return () => {};

      const channel = supabase
        .channel(`messages_v2_${convId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'messages_v2',
          filter: `conversation_id=eq.${convId}`
        }, async (payload) => {
          if (payload.eventType === 'INSERT') {
            const muiMessage = {
              id: payload.new.id,
              conversationId: convId,
              role: payload.new.sender_id === currentUserId ? 'user' : 'assistant',
              status: payload.new.message_status,
              parts: payload.new.content?.parts || [],
              createdAt: payload.new.created_at,
              isEdited: payload.new.is_edited,
              replyTo: payload.new.reply_to_message_id ? {
                id: payload.new.reply_to_message_id,
                content: payload.new.reply_to_content
              } : null
            };
            onUpdate({ type: 'add', message: muiMessage });
          } else if (payload.eventType === 'UPDATE') {
            const muiMessage = {
              id: payload.new.id,
              conversationId: convId,
              role: payload.new.sender_id === currentUserId ? 'user' : 'assistant',
              status: payload.new.message_status,
              parts: payload.new.content?.parts || [],
              createdAt: payload.new.created_at,
              isEdited: payload.new.is_edited,
              replyTo: payload.new.reply_to_message_id ? {
                id: payload.new.reply_to_message_id,
                content: payload.new.reply_to_content
              } : null
            };
            onUpdate({ type: 'update', message: muiMessage });
          } else if (payload.eventType === 'DELETE') {
            onUpdate({ type: 'remove', messageId: payload.old.id });
          }
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    },

    /**
     * Establecer indicador de escritura usando V2
     */
    async setTyping({ conversationId: convId, isTyping }) {
      if (!convId || !currentUserId) return;

      await supabase.rpc('set_typing_indicator_v2', {
        p_conversation_id: convId,
        p_is_typing: isTyping
      });
    },

    /**
     * Marcar mensajes como leídos usando V2
     */
    async markRead({ conversationId: convId, messageId }) {
      if (!convId || !currentUserId) return;

      // Actualizar el campo de lectura correspondiente en la conversación
      const { data: conversation } = await supabase
        .from('conversations_v2')
        .select('participant_a, participant_b')
        .eq('id', convId)
        .single();

      if (!conversation) return;

      const updateField = conversation.participant_a === currentUserId 
        ? 'read_by_a' 
        : 'read_by_b';
      
      const timestampField = conversation.participant_a === currentUserId 
        ? 'read_at_a' 
        : 'read_at_b';

      await supabase
        .from('messages_v2')
        .update({
          [updateField]: true,
          [timestampField]: new Date().toISOString()
        })
        .eq('id', messageId);
    }
  };
}

export default createSupabaseMuiAdapter;
